import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { calculateFinancialSummary, 
  formatCurrency, 
  getVal, 
  setBool, 
  isNegative,
  formatDate
} from '../../helpers/process_morningstar.js';

import {
  Box,
  Container,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from "@mui/material";


export default function InvestedCapital() {
  const tableRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [Tax, setTax] = useState(getVal("selectedTax"));
  const [capRD, setCapRD] = useState(getVal("capRD"));
  const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));

  useEffect(() => {
    if (tableRef.current) {
      const el = tableRef.current;
      el.scrollTo({
        left: el.scrollWidth,
      });
    }
  }, [currentTableData]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };
  
    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };
  
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const financialSummaryData = await calculateFinancialSummary(capRD, includeOtherIncome, Tax);
      setCurrentTableData(financialSummaryData);
    };

    fetchData();
  }, [capRD, includeOtherIncome, Tax]);

  // Helper to update table data
  const updateFinancialSummaryTable = (newData) => {
    setTables(prev =>
      prev.map(t =>
        t.tableName === "Invested Capital" ? { ...t, tableData: newData } : t
      )
    );
    setCurrentTableData(newData);
  };

  if (error) return <div>Error: {error}</div>;

      return (
        <>
            <>

              {currentTableData.length > 0 ? (
              <Box sx={{ mx: "25px"}}>
  
               <TableContainer component={Paper} sx={{ boxShadow: "none", pb: 10 }} ref={tableRef}>             
                
                  <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        position: "sticky",
                        left: 0,
                      }}
                    >
                   Asset Approach
                </Typography>  
                {/* Working Capital */}
                <Table stickyHeader size="small">
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 380
                          }}
                        >
                          Working Capital
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176",
                              borderRight: "1px solid #969696ff", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Current Assets", field: "" },
                      { label: "Cash and Equivalents", field: "cashAndEquivalents" },
                      { label: "Restricted Cash", field: "restrictedCash" },
                      { label: "Marketable Securities", field: "marketableSecurities" },
                      { label: "Accounts Receivable", field: "accountsReceivable" },
                      { label: "Inventory", field: "inventory" },
                      { label: "Prepaid Expenses", field: "prepaidExpenses" },
                      { label: "Current Deferred Assets", field: "currentDeferredAssets" },
                      { label: "Other Current Assets", field: "otherCurrentAssets" },
                      { label: "Total Current Assets", field: "totalCurrentAssets" },
                      { label: "", field: "" },
                      { label: "Current Liabilities", field: "" },
                      { label: "Accounts Payable", field: "accountsPayable" },
                      { label: "Dividends Payable", field: "dividendsPayable" },
                      { label: "Notes Payable", field: "notesPayable" },
                      { label: "Short Term Debt", field: "shortTermDebt" },
                      { label: "Accrued Expenses", field: "accruedExpenses" },
                      { label: "Deferred Revenue", field: "deferredRevenue" },
                      { label: "Current Deferred Income Taxes", field: "currentDeferredIncomeTaxes" },
                      { label: "Other Current Liabilities", field: "otherCurrentLiabilities" },
                      { label: "Other Miscellaneous Liabilities", field: "otherMiscLiabilities" },
                      { label: "Total Current Liabilities", field: "totalCurrentLiabilities" },
                      { label: "", field: "" },
                      { label: "Net Working Capital", field: "netWorkingCapital" },
                      { label: "Net Working Capital (Excl. Debt)", field: "netWorkingCapitalExclDebt" },
                      { label: "Non-Cash Working Capital", field: "nonCashWorkingCapital" },
                    ].map((rowItem, rowIdx) => (
                      <TableRow
                        key={rowIdx}
                        sx={{
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",  
                          }}
                        >
                          {rowItem.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff", 
                            color: isNegative(row[rowItem.field]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[rowItem.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Fixed Capital */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff", 
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 380 
                          }}
                        >
                          Fixed Capital
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Gross Plant", field: "" },
                      { label: "Net Fixed Assets", field: "netPremises" },
                      { label: "Accumulated Depreciation and Depletion", field: "accumulatedDepreciation" },
                      { label: "Gross Fixed Assets (Plant, Prop, & Equipment)", field: "grossFixedAssets" },
                      { label: "Other Fixed Capital", field: "" },
                      { label: "Goodwill", field: "goodwill" },
                      { label: "Intangibles", field: "otherIntangibleAssets" },
                      { label: "Non Current Deferred Income Taxes", field: "nonCurrentDeferredAssets" },
                      { label: "Other Non-Current Assets", field: "otherNonCurrentAssets" },
                      { label: "Investments and Advances", field: "investmentsAndAdvances" },
                      { label: "Total Other Fixed Capital", field: "totalOtherFixedCapital" },
                      { label: "Total NET Fixed Capital (incl. intangibles)", field: "netFixedCapitalInclIntangible" },
                      { label: "Total NET Fixed Capital (excl. intangibles)", field: "netFixedCapitalExclIntangible" },
                      { label: "", field: "" },
                      { label: "Invested Capital (Classic Asset Approach)", field: "investedCapitalAsset" },

                    ].map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",  
                          }}
                        >
                          {item.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff", 
                            color: isNegative(row[item.value]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[item.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

              {/* Financing Approach */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff", 
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 380 
                          }}
                        >
                          Financing Approach
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Short-term Debt", field: "shortTermDebt" },
                      { label: "Long-term Debt", field: "longTermDebt" },
                      { label: "Capital Lease Obligations", field: "capitalLeaseObligations" },
                      { label: "Deferred Income Taxes", field: "deferredIncomeTaxes" },
                      { label: "Other Deferred Tax Assets", field: "otherDeferredTaxAssets" },
                      { label: "Derivative Product Liabilities", field: "derivativeLiabilities" },
                      { label: "Pension and Post-Retirement Benefits", field: "retirementBenefits" },
                      { label: "Long-Term Provisions (Legal)", field: "longTermProvisions" },
                      { label: "Regulatory Liabilities", field: "regulatoryLiabilities" },
                      { label: "Non-Current Other Liabilities", field: "nonCurrentOtherLiabilities" },
                      { label: "Non-Current Miscellaneous Liabilities", field: "nonCurrentMiscLiabilities" },
                      { label: "Minority Interests (Bal. Sheet)", field: "minorityInterestsBal" },
                      { label: "Preferred Securities of Subsidiary Trust", field: "preferredSecurities" },
                      { label: "Preferred Equity Outside Stock Equity", field: "outsideStockEquity" },
                      { label: "Preferred Stock Equity", field: "stockEquity" },
                      { label: "Common Stock Equity", field: "commonStock" },
                      { label: "Invested Caital (Classic Financing Approach)", field: "investedCapitalFinancing" },
                      { label: "", field: "" },
                      { label: "Invested Capital (Classic Asset Approach)", field: "investedCapitalAsset" },
                      { label: "", field: "" },
                      { label: "Difference", field: "investedCapitalDifference" },
                      { label: "", field: "" },
                      { label: "Debt and Equivalents", field: "debtAndEquivalents" },

                    ].map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",  
                          }}
                        >
                          {item.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff", 
                            color: isNegative(row[item.value]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[item.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table> 

              {/* Inflation Adjustments */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff", 
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 380 
                          }}
                        >
                          Inflation Adjustments
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              borderRight: "1px solid #969696ff", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Net Plant / Gross Plant", field: "netPlantOverGrossPlant" },
                      { label: "Asset Life", field: "assetLifeExclIntangible" },
                      { label: "Average Age", field: "averageAge" },
                      { label: "Inflation table in reporting period", field: "inflationTablePeriod" },
                      { label: "Inflation table according to age", field: "inflationTableAge" },
                      { label: "Inflation factor", field: "inflationFactor" },
                      { label: "", field: "" },
                      { label: "Fixed Capital", field: "" },
                      { label: "Gross Fixed Assets (Plant, Prop, & Equipment)", field: "grossFixedAssets" },
                      { label: "Net Fixed Assets", field: "netPremises" },
                      { label: "Goodwill", field: "goodwill" },
                      { label: "Other Intangible Assets", field: "otherIntangibleAssets" },
                      { label: "Non Current Deferred Income Taxes", field: "nonCurrentDeferredAssets" },
                      { label: "Other Non-Current Assets", field: "otherNonCurrentAssets" },
                      { label: "Investments and Advances", field: "investmentsAndAdvances" },
                      { label: "Gross Fixed Capital (Incl. Intangibles)", field: "totalOtherFixedCapitalIncl" },
                      { label: "Gross Fixed Capital (Excl. Intangibles)", field: "totalOtherFixedCapitalExcl" }, 
                      { label: "Net Fixed Capital (Incl. Intangibles)", field: "netFixedCapitalInclIntangible" },
                      { label: "Net Fixed Capital (Excl. Intangibles)", field: "netFixedCapitalExclIntangible" },
                      getVal('includeOtherIncome') ? 
                      { label: "User's Choice: Net Fixed Capital (Incl. Intangibles)", field: "netFixedCapitalInclIntangible" } :
                      { label: "User's Choice: Net Fixed Capital (Excl. Intangibles)", field: "netFixedCapitalExclIntangible" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Net Fixed Capital (Incl. Intangibles) Grw%", field: "netFixedCapitalInclIntangibleGrw" } :
                      { label: "User's Choice: Net Fixed Capital (Excl. Intangibles) Grw%", field: "netFixedCapitalExclIntangibleGrw" },

                    ].map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",  
                          }}
                        >
                          {item.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff", 
                            color: isNegative(row[item.value]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[item.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table> 

                {/* Inflation Adjusted Fixed Capital */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff",  
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                            minWidth: 380
                          }}
                        >
                          Inflation Adjusted Fixed Capital
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff",  
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Infl. Adj. Gross Fixed Capital (Incl. Intangibles)", field: "totalOtherFixedCapitalIncl" },
                      { label: "Infl. Adj. Gross Fixed Capital (Excl. Intangibles)", field: "totalOtherFixedCapitalExcl" },
                      { label: "Infl. Adj. Net Fixed Capital (Incl. Intangibles)", field: "netFixedCapitalInclIntangible" },
                      { label: "Infl. Adj. Net Fixed Capital (Excl. Intangibles)", field: "netFixedCapitalExclIntangible" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Gross Fixed Capital (Incl. Intangibles)", field: "totalOtherFixedCapitalIncl" } :
                      { label: "User's Choice: Infl. Adj. Gross Fixed Capital (Excl. Intangibles)", field: "totalOtherFixedCapitalExcl" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Gross Fixed Capital (Incl. Intangibles) Grw%", field: "InflAdjGrossFixedCapitalInclGrw" } :
                      { label: "User's Choice: Infl. Adj. Gross Fixed Capital (Excl. Intangibles) Grw%", field: "InflAdjGrossFixedCapitalExclGrw" }, 
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Net Fixed Capital (Incl. Intangibles)", field: "netFixedCapitalInclIntangible" } :
                      { label: "User's Choice: Infl. Adj. Net Fixed Capital (Excl. Intangibles)", field: "netFixedCapitalExclIntangible" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Net Fixed Capital (Incl. Intangibles) Grw%", field: "InflAdjNetFixedCapitalInclGrw" } :
                      { label: "User's Choice: Infl. Adj. Net Fixed Capital (Excl. Intangibles) Grw%", field: "InflAdjNetFixedCapitalExclGrw" },
                      { label: "", field: "" },
                    ].map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",   
                          }}
                        >
                          {item.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff",  
                            color: isNegative(row[item.value]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[item.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table> 

                {/* GROSS Total Inflation Adjusted */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff",
                            fontSize: "13px",   
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                            minWidth: 380
                          }}
                        >
                          Total Inflation Adjusted GROSS Invested Capital (WC & FC)
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff",   
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Infl. Adj. Gross Invested Capital (Incl. Intangibles)", field: "InflAdjGrossInvestedCapitalIncl" },
                      { label: "Infl. Adj. Gross Invested Capital (Excl. Intangibles)", field: "InflAdjGrossInvestedCapitalExcl" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Gross Invested Capital (Incl. Intangibles)", field: "InflAdjGrossInvestedCapitalIncl" } :
                      { label: "User's Choice: Infl. Adj. Gross Invested Capital (Excl. Intangibles)", field: "InflAdjGrossInvestedCapitalExcl" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Gross Invested Capital (Incl. Intangibles) Grw%", field: "InflAdjGrossInvestedCapitalInclGrw" } :
                      { label: "User's Choice: Infl. Adj. Gross Invested Capital (Excl. Intangibles) Grw%", field: "InflAdjGrossInvestedCapitalExclGrw" },
                      { label: "", field: "" },
                    ].map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",    
                          }}
                        >
                          {item.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff",   
                            color: isNegative(row[item.value]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[item.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table> 

                {/* NET Total Inflation Adjusted */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB",
                            borderRight: "1px solid #969696ff", 
                            fontWeight: "bold", 
                            fontSize: "13px",
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 380    
                          }}
                        >
                          Total Inflation Adjusted NET Invested Capital (WC & FC)
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff",    
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Infl. Adj. Net Invested Capital (Incl. Intangibles)", field: "InflAdjNetInvestedCapitalIncl" },
                      { label: "Infl. Adj. Net Invested Capital (Excl. Intangibles)", field: "InflAdjNetInvestedCapitalExcl" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Net Invested Capital (Incl. Intangibles)", field: "InflAdjNetInvestedCapitalIncl" } :
                      { label: "User's Choice: Infl. Adj. Net Invested Capital (Excl. Intangibles)", field: "InflAdjNetInvestedCapitalExcl" },
                      getVal('includeOtherIncome') ?
                      { label: "User's Choice: Infl. Adj. Net Invested Capital (Incl. Intangibles) Grw%", field: "InflAdjNetInvestedCapitalInclGrw" } :
                      { label: "User's Choice: Infl. Adj. Net Invested Capital (Excl. Intangibles) Grw%", field: "InflAdjNetInvestedCapitalExclGrw" },
                      { label: "", field: "" },
                    ].map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",     
                          }}
                        >
                          {item.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff",    
                            color: isNegative(row[item.value]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[item.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Plowback, Chart, Market */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff",    
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 380
                          }}
                        >
                          Plowback, Chart, Market
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff",    
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 70.51,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem",
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Plowback", field: "" },
                      { label: "CF from Continuing Operations", field: "cashFlowFromOps" },
                      { label: "Less: After Tax Interest", field: "interestExpenseAT" },
                      { label: "Less: Maintenance CAPEX (Depn)", field: "depreciation" },
                      { label: "Annual Plowback", field: "plowback" },
                      { label: "", field: "" },

                      { label: "For Chart", field: "" },
                      getVal('includeOtherIncome') ?
                      { label: "Infl Adj Gross Capital Growth %", field: "InflAdjGrossInvestedCapitalInclGrw" } :
                      { label: "Infl Adj Gross Capital Growth %", field: "InflAdjGrossInvestedCapitalExclGrw" },
                      { label: "Plowback", field: "plowback" },
                      { label: "", field: "" },

                      { label: "Market Equity", field: "" },
                      { label: "Shares", field: "basicAverageShares" },
                      { label: "Price", field: "price" },
                      { label: "Market Cap", field: "marketCap" },
                      { label: "", field: "" },

                      { label: "EV / EBITDA", field: "" },
                      { label: "EBITDA", field: "ebitda" },
                      { label: "Enterprise Value (EV)", field: "enterpriseValue" },
                      { label: "EV / EBITDA", field: "evOverEbitda" },

                    ].map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",    
                          }}
                        >
                          {item.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff",    
                            color: isNegative(row[item.value]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[item.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>  
              </TableContainer>
              </Box>
            ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "50vh", // full screen height
                    }}
                  >
                    <h3>Loading...</h3>
                    <CircularProgress />
                  </div>
            )}
        </>
    </>
  )}